import React, { useEffect, useState } from "react";
import { supabase } from "./supabase";
import { calculateStreaks } from "./utils/streakCalc";
import "./stats.css";

export default function Stats({ user, onClose }) {
  const [stats, setStats] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [displayName, setDisplayName] = useState(user?.user_metadata?.display_name || "");
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(displayName);
  const [nameSaving, setNameSaving] = useState(false);

  const saveName = async () => {
    if (!nameInput.trim() || nameInput.trim() === displayName) {
      setEditingName(false);
      return;
    }
    setNameSaving(true);
    const { error } = await supabase.auth.updateUser({
      data: { display_name: nameInput.trim() },
    });
    if (!error) {
      setDisplayName(nameInput.trim());
    }
    setNameSaving(false);
    setEditingName(false);
  };

  useEffect(() => {
    async function fetchStats() {
      const { data, error } = await supabase
        .from("game_stats")
        .select("*")
        .eq("user_id", user.id);

      if (error) {
        console.error("Error fetching stats:", error);
        setFetchError("Failed to load stats. Please try again.");
      } else {
        setStats(data);
      }
    }

    fetchStats();
  }, [user, retryCount]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    onClose();
    window.location.reload();
  };

  if (fetchError) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <button className="close-btn" onClick={onClose}>✖</button>
          <p className="error-text">{fetchError}</p>
          <button className="retry-btn" onClick={() => { setFetchError(null); setStats(null); setRetryCount((c) => c + 1); }}>Retry</button>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <p>Loading stats...</p>
        </div>
      </div>
    );
  }

  const totalDays = new Set(stats.map((s) => s.date)).size;
  const wins = stats.filter((s) => s.result === "won").length;
  const totalItems = stats.length;
  const itemWinPct = totalItems ? Math.round((wins / totalItems) * 100) : 0;

  // Group by date to calculate day-level stats
  const byDate = {};
  stats.forEach((s) => {
    if (!byDate[s.date]) byDate[s.date] = [];
    byDate[s.date].push(s);
  });

  // A "perfect day" is getting all items correct
  const perfectDays = Object.values(byDate).filter(
    (dayStats) => dayStats.length >= 5 && dayStats.every((s) => s.result === "won")
  ).length;

  const { currentStreak, bestStreak } = calculateStreaks(stats);

  const averageSolve = wins
    ? Math.round(stats.filter((s) => s.result === "won").reduce((sum, s) => sum + s.guesses, 0) / wins * 10) / 10
    : 0;

  // Guess distribution (per item, not per day)
  const distribution = Array(7).fill(0); // index 0-5 for 1-6 guesses, index 6 for failed
  stats.forEach((s) => {
    if (s.result === "won" && s.guesses >= 1 && s.guesses <= 6) {
      distribution[s.guesses - 1]++;
    } else if (s.result === "loss") {
      distribution[6]++;
    }
  });


  const maxCount = Math.max(...distribution, 1);


  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <button className="close-btn" onClick={onClose}>✖</button>
          <button className="logout-btn" onClick={handleLogout}>Logout</button>
        </div>

        <div className="profile-section">
          {editingName ? (
            <div className="name-edit">
              <input
                type="text"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                maxLength={30}
                autoFocus
                onKeyDown={(e) => e.key === "Enter" && saveName()}
              />
              <button onClick={saveName} disabled={nameSaving}>
                {nameSaving ? "..." : "Save"}
              </button>
              <button onClick={() => { setEditingName(false); setNameInput(displayName); }}>
                Cancel
              </button>
            </div>
          ) : (
            <p className="display-name" onClick={() => setEditingName(true)}>
              {displayName || user.email}
              <span className="edit-hint">✎</span>
            </p>
          )}
        </div>

        <h2>Your Stats</h2>

        <div className="stats-row">
          <div className="stat-block">
            <div className="stat-number">{totalDays}</div>
            <div className="stat-label">Days</div>
          </div>
          <div className="stat-block">
            <div className="stat-number">{itemWinPct}</div>
            <div className="stat-label">Win %</div>
          </div>
          <div className="stat-block">
            <div className="stat-number">{perfectDays}</div>
            <div className="stat-label">Perfect</div>
          </div>
          <div className="stat-block">
            <div className="stat-number">{currentStreak}</div>
            <div className="stat-label">Streak</div>
          </div>
          <div className="stat-block">
            <div className="stat-number">{bestStreak}</div>
            <div className="stat-label">Best Streak</div>
          </div>
          <div className="stat-block">
            <div className="stat-number">{averageSolve}</div>
            <div className="stat-label">Avg Solve</div>
          </div>
        </div>

        <div className="guess-distribution">
          <h3>Guess Distribution</h3>
          {distribution.map((count, i) => (
            <div key={i} className="bar-row">
              <span>{i === 6 ? "X:" : `${i + 1}:`}</span>
              <div className="bar" style={{ width: `${(count / maxCount) * 100}%` }}>
                {count}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
